import { useState } from 'react';

const STORAGE_KEY = 'buddee-age-verified';

/**
 * AgeGate — shown once per browser until the user confirms they are 21+.
 *
 * Legal basis: US federal/state cannabis regulations require age-gating for
 * cannabis-related content. Clicking "I am 21 or older" constitutes the
 * visitor's affirmative declaration of legal age. Clicking "Exit" redirects
 * away from all site content.
 */
export default function AgeGate({ children }) {
  const [verified, setVerified] = useState(
    () => localStorage.getItem(STORAGE_KEY) === '1'
  );

  if (verified) return children;

  const handleVerify = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVerified(true);
  };

  const handleExit = () => {
    window.location.href = 'https://www.google.com';
  };

  return (
    <>
      {/* Overlay — blocks the page completely */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center px-5"
        style={{ backgroundColor: '#0d1a14' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
      >
        <div className="w-full max-w-sm text-center">
          {/* Brand mark */}
          <img
            src={`${process.env.PUBLIC_URL}/chef-buddee.png`}
            alt="Chef Buddee mascot"
            className="h-24 w-auto mb-4 select-none mx-auto"
          />
          <h1
            id="age-gate-title"
            className="text-3xl font-extrabold tracking-tight mb-1"
            style={{ color: '#e6f0e9' }}
          >
            Chef Buddee
          </h1>
          <p className="text-xs italic mb-8" style={{ color: '#6b9b7a' }}>
            by Puff Puff Peace LLC
          </p>

          {/* Age notice */}
          <div
            className="rounded-2xl px-6 py-6 mb-6 border"
            style={{ backgroundColor: '#132419', borderColor: '#1e3d32' }}
          >
            <p
              className="text-lg font-bold mb-2"
              style={{ color: '#e6f0e9' }}
            >
              You must be 21 or older
            </p>
            <p className="text-sm leading-relaxed" style={{ color: '#6b9b7a' }}>
              This site contains cannabis-related content intended for adults
              21 years of age or older where permitted by law. By entering,
              you confirm that you are of legal age in your jurisdiction.
            </p>
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={handleVerify}
              className="w-full py-3 rounded-full font-extrabold text-sm tracking-wide transition hover:opacity-90 active:scale-95"
              style={{ backgroundColor: '#1e3d32', color: '#e6f0e9' }}
            >
              I am 21 or older — Enter
            </button>
            <button
              onClick={handleExit}
              className="w-full py-3 rounded-full font-semibold text-sm transition hover:opacity-70"
              style={{ color: '#6b9b7a' }}
            >
              I am under 21 — Exit
            </button>
          </div>

          {/* Legal disclaimer */}
          <p className="mt-8 text-xs leading-relaxed" style={{ color: '#3d5c48' }}>
            Chef Buddee does not sell cannabis or cannabis-derived products.
            Content is for informational and culinary purposes only. Nothing on
            this site constitutes medical or legal advice. Consume responsibly
            and in accordance with your local laws.
          </p>
        </div>
      </div>

      {/* Hidden page content — still rendered for SEO, but covered by overlay */}
      <div aria-hidden="true" className="sr-only">{children}</div>
    </>
  );
}
